import { useState } from 'react';
import Box from '@mui/material/Box';
import { IconButton } from '../../components/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import { useSidebar } from './useSidebar';
import { SidebarLogo } from './SidebarLogo';
import { SidebarMenu } from './SidebarMenu';
import { SidebarMenuSkeleton } from './SidebarMenuSkeleton';
import { useIsMobile } from '../useIsMobile';
import { alpha } from '@mui/material/styles';

const WIDTH_EXPANDED = 248;
const WIDTH_COLLAPSED = 60;

interface SidebarLayout {
  /** Largura da coluna reservada no LayoutShell (0 quando a sidebar é overlay). */
  placeholderWidth: number;
  navPosition: 'fixed' | 'absolute' | 'relative';
  navWidth: number;
  navHeight: string;
  transform: string;
  transition: string;
  boxShadow: number | 'none';
}

/**
 * Ocupa o slot de sidebar da casca. Apenas composição: o estado e as
 * decisões vêm do `useSidebar`.
 */
export function Sidebar(): JSX.Element {
  const {
    menu,
    logoHeader,
    logoHeaderReduzido,
    collapsed,
    expandedPaths,
    activePath,
    isTransitioning,
    mobileOpen,
    onToggleCollapse,
    onToggleSubmenu,
    onNavigate,
    onLogoClick,
    onCloseMobile,
  } = useSidebar();

  const isMobile = useIsMobile();

  // Espelha o comportamento do sidebar legado (app-sidebar.js): recolhida,
  // ela expande temporariamente enquanto o mouse está sobre ela e volta a
  // recolher ao sair. `collapsed` continua sendo a preferência do usuário
  // (botão de toggle); o hover só afeta a exibição. Irrelevante em mobile,
  // onde a sidebar é sempre exibida por extenso (overlay) ou totalmente oculta.
  const [isHovering, setIsHovering] = useState(false);
  const visuallyCollapsed = !isMobile && collapsed && !isHovering;

  // Espelha o `.nav-toggle` do sidebar legado: é um controle de desktop e some
  // enquanto a sidebar está exibida recolhida (não cabe ao lado do logo mini),
  // reaparecendo na expansão por hover — que é por onde o usuário volta a fixá-la.
  const showCollapseToggle = !isMobile && !visuallyCollapsed;

  // Sem isso, clicar para recolher com o mouse ainda sobre a sidebar não
  // teria efeito visual: `isHovering` continuaria true até o mouse sair,
  // mascarando o toggle. O clique é uma decisão explícita do usuário e deve
  // recolher de imediato, independente do cursor ainda estar em cima.
  function handleToggleCollapse(): void {
    setIsHovering(false);
    onToggleCollapse();
  }

  // Cada estado da sidebar fixa o conjunto inteiro de dimensões. Recolhida, ela
  // sai do fluxo (absolute) e mantém reservada apenas a coluna estreita, para que
  // a expansão por hover aconteça por cima do conteúdo sem empurrá-lo.
  function resolveLayout(): SidebarLayout {
    // Overlay mobile: não ocupa coluna e desliza de fora da tela.
    if (isMobile) {
      return {
        placeholderWidth: 0,
        navPosition: 'fixed',
        navWidth: WIDTH_EXPANDED,
        navHeight: '100vh',
        transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.3s',
        boxShadow: 8,
      };
    }

    // Recolhida em repouso.
    if (visuallyCollapsed) {
      return {
        placeholderWidth: WIDTH_COLLAPSED,
        navPosition: 'absolute',
        navWidth: WIDTH_COLLAPSED,
        navHeight: '100%',
        transform: 'none',
        transition: 'width 0.2s',
        boxShadow: 'none',
      };
    }

    // Recolhida, expandida temporariamente pelo hover: flutua sobre o conteúdo,
    // então precisa de sombra para se separar dele.
    if (collapsed) {
      return {
        placeholderWidth: WIDTH_COLLAPSED,
        navPosition: 'absolute',
        navWidth: WIDTH_EXPANDED,
        navHeight: '100%',
        transform: 'none',
        transition: 'width 0.2s',
        boxShadow: 4,
      };
    }

    // Fixada por extenso: ocupa coluna própria, dispensa sombra.
    return {
      placeholderWidth: WIDTH_EXPANDED,
      navPosition: 'relative',
      navWidth: WIDTH_EXPANDED,
      navHeight: '100%',
      transform: 'none',
      transition: 'width 0.2s',
      boxShadow: 'none',
    };
  }

  const layout = resolveLayout();

  return (
    <>
      {/* Placeholder que reserva o espaço no layout em flex row (LayoutShell). Em
          mobile não reserva espaço algum: a sidebar deixa de ocupar uma coluna e
          passa a ser um overlay posicionado sobre o conteúdo, escondida por padrão. */}
      <Box
        sx={{
          position: 'relative',
          width: layout.placeholderWidth,
          height: '100%',
          flexShrink: 0,
        }}
      >
        <Box
          component="nav"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          sx={{
            position: layout.navPosition,
            top: 0,
            left: 0,
            width: layout.navWidth,
            height: layout.navHeight,
            // Em mobile o overlay fica totalmente oculto à esquerda quando fechado;
            // abrir/fechar só o desliza, sem afetar o layout do conteúdo.
            transform: layout.transform,
            transition: layout.transition,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            borderRight: 1,
            borderColor: (theme) => alpha(theme.palette.common.white, 0.2),
            zIndex: (theme) => theme.zIndex.drawer,
            boxShadow: layout.boxShadow,
          }}
        >
          {/* O padding à direita reserva o espaço do toggle: sem ele o logo, que
              ocupa a largura inteira do cabeçalho, passaria por baixo do ícone. */}
          <Box sx={{ position: 'relative', pr: showCollapseToggle ? 4 : 0 }}>
            <SidebarLogo
              logoHeader={logoHeader}
              logoHeaderReduzido={logoHeaderReduzido}
              collapsed={visuallyCollapsed}
              onLogoClick={onLogoClick}
            />
            {showCollapseToggle && (
              <IconButton
                onClick={handleToggleCollapse}
                aria-label="Recolher/expandir menu"
                size="small"
                color="inherit"
                sx={{
                  position: 'absolute',
                  top: '50%',
                  right: 8,
                  transform: 'translateY(-50%)',
                  fontSize: 20,
                  opacity: 0.55,
                  '&:hover': { opacity: 1 },
                }}
              >
                {/* Glifos da fonte centericon, os mesmos do toggle legado: "fixar"
                    quando expandida, "minimizar" quando a preferência é recolher. */}
                <Box
                  component="i"
                  className={`centericon ${collapsed ? 'centericon-minimizar_menu' : 'centericon-fixar_menu'}`}
                  sx={{ fontSize: 'inherit' }}
                />
              </IconButton>
            )}
            {isMobile && (
              <IconButton
                onClick={onCloseMobile}
                aria-label="Fechar menu"
                size="small"
                color="inherit"
                sx={{ position: 'absolute', top: 8, right: 8 }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              // Reproduz o visual do perfect-scrollbar do sidebar legado (barra fina,
              // sem track, só aparece no hover do menu) em vez da scrollbar nativa do OS.
              scrollbarWidth: 'thin',
              scrollbarColor: 'transparent transparent',
              '&:hover': {
                scrollbarColor: (theme) => `${alpha(theme.palette.common.black, 0.4)} transparent`,
              },
              '&::-webkit-scrollbar': {
                width: 8,
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'transparent',
                borderRadius: 4,
              },
              '&:hover::-webkit-scrollbar-thumb': {
                background: (theme) => alpha(theme.palette.common.black, 0.4),
              },
            }}
          >
            {isTransitioning ? (
              <SidebarMenuSkeleton collapsed={visuallyCollapsed} />
            ) : (
              <SidebarMenu
                items={menu}
                depth={0}
                expandedPaths={expandedPaths}
                activePath={activePath}
                collapsed={visuallyCollapsed}
                onToggleSubmenu={onToggleSubmenu}
                onNavigate={onNavigate}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Backdrop: fecha o overlay ao tocar fora dele, espelhando o handler de
          clique fora do sidebar legado (`$('html').on('click', ...)`). */}
      {isMobile && mobileOpen && (
        <Box
          onClick={onCloseMobile}
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: (theme) => alpha(theme.palette.common.black, 0.5),
            zIndex: (theme) => theme.zIndex.drawer - 1,
          }}
        />
      )}
    </>
  );
}
